import React from 'react';

import {AssignmentsListV2} from 'client/Assignments/GlobalAssignments/V2/AssignmentsListV2';
import {TeacherDashboardLayout} from 'client/TeacherHomepage/Layout/TeacherDashboardLayout';
import {useHasTeacherDashboardV2} from 'client/TeacherHomepage/hooks/useHasTeacherDashboardV2';

import {ClassDetailEntryFlows} from './Classroom/Teacher/V2/ClassDetailEntryFlows';
import {ClassDetailHeader} from './Classroom/Teacher/V2/ClassDetailHeader';
import Classrooms from './Classrooms.react';
import SecondaryHeader from './shared/SecondaryHeader';

interface Props {
  children?: React.ReactNode;
  location?: {pathname?: string};
  params?: {classId?: string};
  [routeProp: string]: unknown;
}

const CLASS_ASSIGNMENTS_PATH = /^\/classes\/[^/]+\/assignments\/?$/;

/**
 * Chrome for all /classes routes. Flag-on teachers get the dashboard shell on
 * the class-assignments page; every other route (and flag-off) keeps the
 * legacy SecondaryHeader chrome.
 *
 * The v2 branch renders its content directly instead of the route children:
 * the legacy Classroom container's awaitMandarkQueries HOC has no subscription
 * of its own (legacy chrome's store-driven re-renders woke it up), and the
 * scoped list only needs the classroom id — mounting it immediately gives one
 * continuous load instead of chained spinners.
 */
const ClassroomsChromeWrapper = ({children, ...rest}: Props) => {
  const {hasTeacherDashboardV2} = useHasTeacherDashboardV2();

  const pathname: string = rest.location?.pathname ?? '';
  const classroomId = rest.params?.classId;

  const legacyChrome = (
    <>
      <SecondaryHeader {...rest} aria-label='secondary' />
      <Classrooms {...rest}>{children}</Classrooms>
    </>
  );

  if (!hasTeacherDashboardV2 || !classroomId || !CLASS_ASSIGNMENTS_PATH.test(pathname)) {
    return legacyChrome;
  }

  return (
    <TeacherDashboardLayout
      header={<ClassDetailHeader classroomId={classroomId} />}
      pathname={pathname}
    >
      {/* Keyed so switching classes re-awaits the new class's query. */}
      <ClassDetailEntryFlows classroomId={classroomId} key={classroomId} />
      <AssignmentsListV2 classroomId={classroomId} />
    </TeacherDashboardLayout>
  );
};

export default ClassroomsChromeWrapper;
